import { useQuery, useMutation, useQueryClient, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type {
  LiveStreamItem,
  LiveStreamListResponse,
  LiveStreamMonitorStatusResponse,
  RecordingItem,
  ConnectionItem,
  StatusSummaryData,
} from './live-live-streams-types';

// ============================================
// Query keys
// ============================================

export const liveLiveStreamKeys = {
  all: ['livestream', 'live_streams'] as const,
  list: (serviceId: string, page: number, perPage: number, search?: string, status?: string) =>
    ['livestream', 'live_streams', 'list', serviceId, { page, perPage, search, status }] as const,
  detail: (serviceId: string, streamId: string) =>
    ['livestream', 'live_streams', serviceId, streamId, 'detail'] as const,
  recordings: (serviceId: string, streamId: string) =>
    ['livestream', 'live_streams', serviceId, streamId, 'recordings'] as const,
  connections: (serviceId: string, streamId: string) =>
    ['livestream', 'live_streams', serviceId, streamId, 'connections'] as const,
  statusSummary: (serviceId: string) =>
    ['livestream', 'live_streams', 'status-summary', serviceId] as const,
  monitorStatus: (serviceId: string, streamId: string) =>
    ['livestream', 'live_streams', serviceId, streamId, 'monitor-status'] as const,
};

// ============================================
// Queries
// ============================================

export function useLiveStreams(
  serviceId: string,
  page: number,
  perPage: number,
  search?: string,
  status?: string,
  options?: Omit<UseQueryOptions<LiveStreamListResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<LiveStreamListResponse>({
    queryKey: liveLiveStreamKeys.list(serviceId, page, perPage, search, status),
    enabled: !!serviceId,
    ...options,
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        per_page: String(perPage),
      };
      if (search) params.search = search;
      if (status && status !== '-1') params.status = status;
      const res = await serviceApiV3.get(mediaBase(serviceId, 'streams'), { params });
      return res.data?.data ?? res.data ?? { count: 0, items: [], page, per_page: perPage, provider_total: 0, returned_count: 0, skipped_count: 0, total: 0 };
    },
  });
}

/** Single live stream by id — unwraps the `{ data }` envelope to the item. */
export function useLiveStream(
  serviceId: string,
  id: string,
  options?: Omit<UseQueryOptions<LiveStreamItem>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<LiveStreamItem>({
    queryKey: liveLiveStreamKeys.detail(serviceId, id),
    enabled: !!serviceId && !!id,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/${id}`);
      return res.data?.data ?? res.data;
    },
  });
}

export function useRecordings(
  serviceId: string,
  streamId: string,
  options?: Omit<UseQueryOptions<RecordingItem[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<RecordingItem[]>({
    queryKey: liveLiveStreamKeys.recordings(serviceId, streamId),
    enabled: !!serviceId && !!streamId,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/${streamId}/records`);
      return res.data?.data ?? [];
    },
  });
}

export function useConnections(
  serviceId: string,
  streamId: string,
  options?: Omit<UseQueryOptions<ConnectionItem[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ConnectionItem[]>({
    queryKey: liveLiveStreamKeys.connections(serviceId, streamId),
    enabled: !!serviceId && !!streamId,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/${streamId}/connections`);
      return res.data?.data ?? [];
    },
  });
}

/**
 * Status summary across all live streams — used by the Dashboard page.
 */
export function useStatusSummary(
  serviceId: string,
  options?: Omit<UseQueryOptions<StatusSummaryData>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<StatusSummaryData>({
    queryKey: liveLiveStreamKeys.statusSummary(serviceId),
    enabled: !!serviceId,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/status-summary`);
      return res.data?.data ?? res.data;
    },
  });
}

/**
 * HTTP polling fallback for the live stream monitor — used when the WebSocket
 * connection fails or errors. Polls GET /streams/:id/monitor/status.
 * Poll interval is 5 s to match the WS heartbeat cadence (15 s heartbeat,
 * data frames arrive ~every few seconds from the provider).
 */
export function useMonitorStatus(
  serviceId: string,
  streamId: string,
  options?: Omit<UseQueryOptions<LiveStreamMonitorStatusResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<LiveStreamMonitorStatusResponse>({
    queryKey: liveLiveStreamKeys.monitorStatus(serviceId, streamId),
    enabled: !!serviceId && !!streamId,
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
    retry: false,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/${streamId}/monitor/status`);
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useDeleteLiveStream(
  serviceId: string,
  options?: UseMutationOptions<void, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (streamId: string) => {
      await serviceApiV3.delete(`${mediaBase(serviceId, 'streams')}/${streamId}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: liveLiveStreamKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/** Forbid a live stream — prevents new ingest connections. */
export function useForbidLiveStream(
  serviceId: string,
  options?: UseMutationOptions<void, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (streamId: string) => {
      await serviceApiV3.post(`${mediaBase(serviceId, 'streams')}/${streamId}/forbid`, {});
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: liveLiveStreamKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/** Reset a live stream — re-enables ingest after a forbid. */
export function useResetLiveStream(
  serviceId: string,
  options?: UseMutationOptions<void, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (streamId: string) => {
      await serviceApiV3.post(`${mediaBase(serviceId, 'streams')}/${streamId}/reset`, {});
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: liveLiveStreamKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/** Trigger re-delivery of a recording completion webhook. */
export function useResendRecordingNotification(
  serviceId: string,
  streamId: string,
  options?: UseMutationOptions<void, Error, string>,
) {
  return useMutation<void, Error, string>({
    mutationFn: async (uuid: string) => {
      await serviceApiV3.post(
        `${mediaBase(serviceId, 'streams')}/${streamId}/records/${uuid}/notify`,
        {},
      );
    },
    ...options,
  });
}

/** Merge a set of recording segments into a single output. */
export function useMergeRecordings(
  serviceId: string,
  streamId: string,
  options?: UseMutationOptions<void, Error, string[]>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string[]>({
    mutationFn: async (uuids: string[]) => {
      await serviceApiV3.post(
        `${mediaBase(serviceId, 'streams')}/${streamId}/records/merge`,
        { uuids },
      );
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: liveLiveStreamKeys.recordings(serviceId, streamId) });
      options?.onSuccess?.(...args);
    },
  });
}
